import { Button, cn, SegmentedControl, TimePicker } from '@nexo/ui';
import type { QuietHours } from '@nexo/types';
import { useMutation } from '@tanstack/react-query';
import { BellRing, Mail, Moon, Volume2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useAuth, useMe } from '../auth/AuthProvider';
import { useEffect, useState, type ReactNode } from 'react';
import {
  alertPrefs,
  notificationPermission,
  playSound,
  requestNotificationPermission,
  useAlertPrefs,
  type PermissionState,
} from './alerts';

/** Sound / system notification preferences (stored per device). */
export function AlertSettings() {
  const prefs = useAlertPrefs();
  const [permission, setPermission] = useState<PermissionState>('default');
  const { t } = useTranslation();

  useEffect(() => {
    void notificationPermission().then(setPermission);
  }, []);

  return (
    <section className="mt-4 rounded-2xl border border-border bg-surface p-6 shadow-panel">
      <h2 className="mb-1 text-sm font-semibold">{t('Notificaciones')}</h2>
      <p className="mb-4 text-xs text-muted">{t('Se guardan en este dispositivo.')}</p>
      <div className="flex flex-col gap-3">
        <Toggle
          icon={<Volume2 />}
          label={t('Sonidos')}
          hint={t('Mensajes, menciones y llamadas')}
          checked={prefs.sound}
          onChange={(sound) => {
            alertPrefs.set({ sound });
            if (sound) playSound('message', true);
          }}
        />
        <Toggle
          icon={<BellRing />}
          label={t('Notificaciones del sistema')}
          hint={t('Cuando Nexo está en segundo plano')}
          checked={prefs.desktop}
          onChange={(desktop) => {
            alertPrefs.set({ desktop });
            if (desktop && permission === 'default')
              void requestNotificationPermission().then(setPermission);
          }}
        />
        {prefs.desktop && permission !== 'granted' && (
          <div className="rounded-lg bg-surface-2 p-3 text-xs text-muted">
            {permission === 'denied' ? (
              t(
                'Las notificaciones están bloqueadas: actívalas en los ajustes del navegador o del sistema.',
              )
            ) : permission === 'unsupported' ? (
              t('Este navegador no admite notificaciones.')
            ) : (
              <div className="flex items-center justify-between gap-2">
                {t('Falta el permiso para mostrar notificaciones.')}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void requestNotificationPermission().then(setPermission)}
                >
                  {t('Permitir')}
                </Button>
              </div>
            )}
          </div>
        )}
        <QuietHoursSetting />
        <DigestSetting />
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm">
            {t('En los canales, por defecto')}
            <span className="block text-xs text-muted">
              {t(
                'Cámbialo en cada conversación desde su menú «…». Los mensajes directos siempre avisan.',
              )}
            </span>
          </span>
          <SegmentedControl
            label={t('En los canales, por defecto')}
            size="sm"
            value={prefs.scope}
            onChange={(scope) => alertPrefs.set({ scope })}
            options={[
              { value: 'all', label: t('Todos los mensajes') },
              { value: 'mentions', label: t('Solo @menciones') },
            ]}
          />
        </div>
      </div>
    </section>
  );
}

/** Daily do-not-disturb window (stored in the account, applies on every device). */
function QuietHoursSetting() {
  const me = useMe();
  const { setUser } = useAuth();
  const [start, setStart] = useState(me.quietHours?.start ?? '22:00');
  const [end, setEnd] = useState(me.quietHours?.end ?? '08:00');
  const { t } = useTranslation();
  const save = useMutation({
    mutationFn: (quietHours: QuietHours | null) => api.users.setPreferences({ quietHours }),
    onSuccess: setUser,
  });
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const enabled = me.quietHours !== null;
  const apply = (next: { start: string; end: string }) =>
    save.mutate({ start: next.start, end: next.end, timeZone });

  return (
    <div className="flex flex-col gap-2">
      <Toggle
        icon={<Moon />}
        label={t('Horario sin avisos')}
        hint={t('«No molestar» automático cada día (en todos tus dispositivos)')}
        checked={enabled}
        onChange={(on) => (on ? apply({ start, end }) : save.mutate(null))}
      />
      {enabled && (
        <div className="ml-7 flex items-center gap-2 text-sm">
          {t('De')}
          <TimePicker
            aria-label={t('Desde')}
            size="sm"
            className="w-auto"
            value={start}
            onChange={(value) => {
              setStart(value);
              apply({ start: value, end });
            }}
          />
          {t('a')}
          <TimePicker
            aria-label={t('Hasta')}
            size="sm"
            className="w-auto"
            value={end}
            onChange={(value) => {
              setEnd(value);
              apply({ start, end: value });
            }}
          />
          <span className="text-xs text-subtle">({timeZone})</span>
        </div>
      )}
    </div>
  );
}

function DigestSetting() {
  const me = useMe();
  const { setUser } = useAuth();
  const { t } = useTranslation();
  const save = useMutation({
    mutationFn: (dailyDigest: boolean) => api.users.setPreferences({ dailyDigest }),
    onSuccess: setUser,
  });
  return (
    <Toggle
      icon={<Mail />}
      label={t('Resumen diario por email')}
      hint={t('Cada mañana: tus tareas de hoy, las vencidas y lo que te has perdido')}
      checked={me.dailyDigest}
      onChange={(on) => save.mutate(on)}
    />
  );
}

function Toggle({
  icon,
  label,
  hint,
  checked,
  onChange,
}: {
  icon: ReactNode;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-3">
      <span className="text-muted [&_svg]:size-4">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm">{label}</span>
        <span className="block text-xs text-muted">{hint}</span>
      </span>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="peer sr-only"
      />
      <span
        aria-hidden
        className={cn(
          'relative h-5 w-9 shrink-0 rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-accent',
          checked ? 'bg-accent' : 'bg-border-strong',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 size-4 rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-4.5' : 'translate-x-0.5',
          )}
        />
      </span>
    </label>
  );
}
